/**
 * Invoices Auto-Select Utilities
 * Handles intelligent auto-selection of sellers based on company selection
 * Matches pattern from EditUserManagement for consistency
 */

/**
 * Extract company IDs from a seller object
 * Handles multiple formats: single number, comma-separated string, or array
 * @param {Object} seller - Seller object with companyIds or companyId property
 * @returns {Array<number>} Normalized array of company IDs
 */
export const getSellerCompanyIds = (seller) => {
    if (!seller) return [];
    const raw = seller.companyIds ?? seller.companyId;
    if (raw === null || raw === undefined || raw === '') return [];
    if (Array.isArray(raw)) {
        return raw.map(Number).filter((v) => !Number.isNaN(v));
    }
    return String(raw)
        .split(',')
        .map((v) => Number(v.trim()))
        .filter((v) => !Number.isNaN(v));
};

/**
 * Auto-select sellers based on company selection
 * Only selects a seller when there is exactly 1 seller for a company
 * Idempotent: preserves existing selections and prevents duplicates
 *
 * @param {Array} fetchedSellers - List of sellers to choose from (with company mappings)
 * @param {Array} selectedCompanyIds - List of selected companies (can be objects with Id or raw numbers)
 * @param {Array} currentSelection - Existing seller selections to preserve (default: [])
 * @returns {Array} Merged list: current selections + new auto-selected sellers
 *
 * Algorithm:
 * 1. For each selected company, find all sellers that map to it
 * 2. If exactly 1 seller exists for that company, add it to selections
 * 3. If company already has a seller, skip it (prevent duplicates)
 * 4. Return merged array (existing + newly auto-selected)
 */
export const computeAutoSelectedSellers = (fetchedSellers, selectedCompanyIds, currentSelection = []) => {
    if (!Array.isArray(fetchedSellers) || fetchedSellers.length === 0) return currentSelection || [];
    if (!Array.isArray(selectedCompanyIds) || selectedCompanyIds.length === 0) return currentSelection || [];

    // Normalize company IDs to numeric values (handle both objects and primitives)
    const normalizedCompanyIds = new Set(
        selectedCompanyIds
            .map((c) => (typeof c === 'object' && c !== null ? c.Id ?? c.id : c))
            .filter((v) => v != null && !(typeof v === 'string' && v.trim() === ''))
            .map(Number)
            .filter((v) => !Number.isNaN(v))
    );

    // Preserve existing selections
    const existing = Array.isArray(currentSelection) ? [...currentSelection] : [];
    const sellerKey = (s) => s?.id ?? s?.value;
    const existingKeys = new Set(existing.map(sellerKey));

    // Track which companies already have a seller selected
    const companiesAlreadyCovered = new Set();
    existing.forEach((s) => {
        getSellerCompanyIds(s).forEach((cid) => companiesAlreadyCovered.add(cid));
    });

    // Auto-select sellers for companies with exactly one mapped seller
    normalizedCompanyIds.forEach((cid) => {
        if (companiesAlreadyCovered.has(cid)) return; // Company already has a seller

        const matches = fetchedSellers.filter((s) => getSellerCompanyIds(s).includes(cid));

        if (matches.length === 1) {
            // Only auto-select if exactly 1 seller exists
            const pick = matches[0];
            const key = sellerKey(pick);
            if (key !== undefined && !existingKeys.has(key)) {
                existing.push(pick);
                existingKeys.add(key);
            }
        }
    });

    return existing;
};

/**
 * Check if a seller is valid for a given company selection
 * Used to validate that selected sellers belong to selected companies
 * @param {Array} selectedCompanies - Selected company IDs
 * @param {Array} selectedSellers - Selected sellers
 * @returns {boolean} True if all sellers have at least one matching company
 */
export const validateSellersForCompanies = (selectedCompanies, selectedSellers) => {
    if (!Array.isArray(selectedSellers) || selectedSellers.length === 0) return true;
    if (!Array.isArray(selectedCompanies) || selectedCompanies.length === 0) return false;

    const normalizedCompanyIds = new Set(
        selectedCompanies
            .map((c) => (typeof c === 'object' && c !== null ? c.Id ?? c.id : c))
            .filter((v) => v != null)
            .map(Number)
            .filter((v) => !Number.isNaN(v))
    );

    return selectedSellers.every((seller) => {
        const sellerCompanies = getSellerCompanyIds(seller);
        return sellerCompanies.some((cid) => normalizedCompanyIds.has(cid));
    });
};
